import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EyeIcon, EyeOffIcon } from './shell/icons';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  rightSlot?: ReactNode;
}

// Forward the ref so libraries that bind by ref — react-hook-form's `register()`
// returns a ref callback that registers the input with the form — actually
// reach the underlying <input>. Without forwardRef, React 18 silently drops
// refs passed to function components, RHF never sees the field, and submit
// runs validation against an empty internal store regardless of the DOM
// state.
export const InputField = forwardRef<HTMLInputElement, Props>(function InputField(
  { label, error, hint, rightSlot, id, type = 'text', ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  const inputId = id ?? rest.name;
  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);
  const effectiveType = isPassword && revealed ? 'text' : type;

  return (
    <label htmlFor={inputId} className="block text-sm">
      <span className="mb-1 flex items-center justify-between text-foreground">
        <span className="font-medium">{label}</span>
        {rightSlot}
      </span>
      <span className="relative block">
        <input
          id={inputId}
          ref={ref}
          type={effectiveType}
          {...rest}
          aria-invalid={error ? true : undefined}
          className={`block h-9 w-full rounded-md border border-input bg-background px-3 ${isPassword ? 'pe-9' : ''} py-1.5 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1`}
        />
        {isPassword ? (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            tabIndex={-1}
            aria-label={revealed ? t('common.hide_password') : t('common.show_password')}
            title={revealed ? t('common.hide_password') : t('common.show_password')}
            data-testid={`${inputId ?? 'password'}-reveal`}
            className="absolute end-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {revealed ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
          </button>
        ) : null}
      </span>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </label>
  );
});

interface ErrorBannerProps {
  children: ReactNode;
}

export function ErrorBanner({ children }: ErrorBannerProps) {
  return (
    <div
      role="alert"
      className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
    >
      {children}
    </div>
  );
}

interface SubmitProps {
  loading: boolean;
  label: string;
  loadingLabel: string;
}

export function SubmitButton({ loading, label, loadingLabel }: SubmitProps) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="inline-flex h-9 w-full items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? loadingLabel : label}
    </button>
  );
}
